import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import type { CertificationSummary } from 'src/api/dataVault/historyCertifications';
import { useDataVault } from 'src/contexts/DataVaultContext';
import CertificationHistoryIcon from 'src/icons/CertificationHistoryIcon';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';
import { HistoryPreviewSkeleton } from './HistoryPreviewSkeleton';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface HistoryPreviewProps {
  certifications: CertificationSummary[];
  actingOnMemberId: number | null;
  isLoading?: boolean;
}

interface CertSectionProps {
  title: string;
  certs: CertificationSummary[];
  actingOnMemberId: number | null;
  emptyMessage: string;
  onCardClick?: (cert: CertificationSummary) => void;
  loadingCertId?: number | null;
}

// ---------------------------------------------------------------------------
// CertSection — section heading + list
// ---------------------------------------------------------------------------

function CertSection({
  title,
  certs,
  actingOnMemberId,
  emptyMessage,
  onCardClick,
  loadingCertId,
}: CertSectionProps): React.ReactElement {
  const visible = certs.slice(0, MAX_DISPLAY);

  return (
    <Stack spacing={1.5}>
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>

      {visible.length === 0 ? (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
            display: 'flex',
            justifyContent: 'center',
            py: 3,
            px: 2.5,
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        visible.map((cert, index) => (
          <CertCard
            key={cert.id}
            cert={cert}
            index={index}
            actingOnMemberId={actingOnMemberId}
            onClick={onCardClick ? () => onCardClick(cert) : undefined}
            isLoadingCard={loadingCertId === cert.id}
          />
        ))
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_DISPLAY = 3;

function iconColors(theme: Theme): { bg: string; fg: string }[] {
  return [
    { bg: '#F0EDFF', fg: '#5B3FBD' }, // even — purple (AI brand)
    {
      bg: alpha(theme.palette.warning.main, 0.12),
      fg: theme.palette.warning.dark,
    }, // odd — orange
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '';
  }
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function certPath(certId: number, actingOnMemberId: number | null): string {
  const base = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/history`
    : '/renter/data-vault/history';
  return `${base}/certifications/${certId}`;
}

// ---------------------------------------------------------------------------
// CertCard — shared card rendering
// ---------------------------------------------------------------------------

function CertCard({
  cert,
  index,
  actingOnMemberId,
  onClick,
  isLoadingCard = false,
}: {
  cert: CertificationSummary;
  index: number;
  actingOnMemberId: number | null;
  onClick?: () => void;
  isLoadingCard?: boolean;
}): React.ReactElement {
  const theme = useTheme();
  const palette = iconColors(theme)[index % 2];
  const date = formatDate(cert.effective_date ?? null);

  const content = (
    <>
      {/* Icon circle */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          bgcolor: palette.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CertificationHistoryIcon
          size={20}
          color={palette.fg}
          style={{ marginLeft: '4px' }}
        />
      </Box>

      {/* Text content */}
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: 13,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {cert.cert_type_label || '\u2014'}
        </Typography>
        {date ? (
          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
            {date}
          </Typography>
        ) : null}
        {cert.property_name ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cert.property_name}
          </Typography>
        ) : null}
        {cert.unit_label || cert.log_number ? (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {cert.unit_label ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                Unit {cert.unit_label}
              </Typography>
            ) : null}
            {cert.unit_label && cert.log_number ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                -
              </Typography>
            ) : null}
            {cert.log_number ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                Log #{cert.log_number}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
        {cert.household_name ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cert.household_name}
          </Typography>
        ) : null}
      </Stack>

      {isLoadingCard ? (
        <CircularProgress
          size={20}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
          aria-label="Loading"
        />
      ) : (
        <ChevronRight size={18} color={theme.palette.text.disabled} />
      )}
    </>
  );

  const sx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 2,
    bgcolor: 'background.paper',
    borderRadius: '14px',
    boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
    textDecoration: 'none',
    color: 'inherit',
    ...(isLoadingCard ? { opacity: 0.7, cursor: 'wait' } : {}),
  };

  if (onClick) {
    return (
      <Box
        component="button"
        onClick={onClick}
        disabled={isLoadingCard}
        sx={{
          ...sx,
          border: 'none',
          width: '100%',
          cursor: isLoadingCard ? 'wait' : 'pointer',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign: 'left',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      key={cert.id}
      component={Link}
      to={certPath(cert.id, actingOnMemberId)}
      sx={sx}
    >
      {content}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// HistoryPreview
// ---------------------------------------------------------------------------

export function HistoryPreview({
  certifications,
  actingOnMemberId,
  isLoading = false,
}: HistoryPreviewProps): React.ReactElement {
  const navigate = useNavigate();
  const { isOnBehalf, targetUserId } = useDataVault();
  const [loadingCertId, setLoadingCertId] = useState<number | null>(null);

  const handleInProgressClick = useCallback(
    async (cert: CertificationSummary) => {
      const surveyId = cert.survey_id;
      if (!surveyId) {
        return;
      }
      setLoadingCertId(cert.id);
      try {
        if (!cert.needs_action) {
          navigate(
            isOnBehalf && targetUserId
              ? `/${RENTER_PATHS.HOME}/survey/${surveyId}/members/${targetUserId}`
              : `/${RENTER_PATHS.HOME}/survey/${surveyId}`
          );
          return;
        }
        const { redirect_to: redirectTo } = await fetchContinueCertification(
          surveyId
        );
        const base = `/${RENTER_PATHS.HOME}/interview/${surveyId}`;
        if (redirectTo === 'data-transfer') {
          navigate(
            isOnBehalf && targetUserId
              ? `${base}/data-transfer/members/${targetUserId}`
              : `${base}/data-transfer`
          );
        } else {
          navigate(
            isOnBehalf && targetUserId
              ? `${base}/members/${targetUserId}`
              : base
          );
        }
      } finally {
        setLoadingCertId(null);
      }
    },
    [navigate, isOnBehalf, targetUserId]
  );

  if (isLoading) {
    return <HistoryPreviewSkeleton count={MAX_DISPLAY} />;
  }

  const allCerts = certifications ?? [];

  const inProgress = allCerts.filter(
    (cert) => cert.is_done === false && cert.is_active === true
  );

  const historical = allCerts.filter(
    (cert) => cert.is_done === true && cert.is_active === false
  );

  return (
    <Stack spacing={1.5}>
      <Typography
        sx={{
          color: 'text.primary',
          fontSize: 17,
          fontWeight: 600,
        }}
      >
        My Application & Re-certification Files
      </Typography>
      <CertSection
        title="Current"
        certs={inProgress}
        actingOnMemberId={actingOnMemberId}
        emptyMessage="No applications or re-certifications in progress."
        onCardClick={handleInProgressClick}
        loadingCertId={loadingCertId}
      />

      <CertSection
        title="History"
        certs={historical}
        actingOnMemberId={actingOnMemberId}
        emptyMessage="No application or re-certification history yet."
      />
    </Stack>
  );
}
